import { SalesData } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

const mapOrder = (o: any): SalesData => ({
  id: o.id,
  invoiceId: (o.order_number as string) ?? o.id,
  requestOrderNumber: (o.order_number as string) ?? o.id,
  fulfillmentOrderNumber: (o.stripe_payment_intent_id as string) ?? '',
  superAdminId: o.user_id ?? '',
  superAdminName: o.metadata?.super_admin_name ?? '',
  instanceId: o.metadata?.instance_id ?? '',
  instanceName: o.metadata?.instance_name ?? '',
  planName: o.metadata?.plan_name ?? '',
  billingCycle: (o.metadata?.billing_cycle as any) ?? 'monthly',
  amount: Number(o.amount ?? 0),
  date: new Date(o.paid_at ?? o.created_at),
  status: (o.status === 'paid' ? 'paid' : o.status === 'pending' ? 'pending' : 'failed') as any,
  isRecurring: Boolean(o.metadata?.is_recurring ?? false),
});

/**
 * Retained for backward-compatibility as a *starter* empty array; consumers should call
 * `fetchSalesData()` for live results.
 */
export const mockSalesData: SalesData[] = [];

export const fetchSalesData = async (): Promise<SalesData[]> => {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) {
    logger.error('fetchSalesData failed', error);
    return [];
  }
  return (data ?? []).map(mapOrder);
};

export const calculateTotalSales = (data: SalesData[]): number =>
  data.reduce((total, sale) => total + sale.amount, 0);

export const filterSalesByStatus = (data: SalesData[], status: string): SalesData[] =>
  data.filter((sale) => sale.status === status);

/**
 * Monthly revenue trend derived live from paid orders (last 6 months).
 * Kept as an empty seed for legacy imports; call `fetchMonthlyRevenue()` for live values.
 */
export const monthlyRevenueData: Array<{
  month: string;
  totalRevenue: number;
  masterLicenseShare: number;
  resellerShare: number;
}> = [];

export const fetchMonthlyRevenue = async () => {
  const { data, error } = await supabase
    .from('orders')
    .select('amount, paid_at, created_at, status')
    .eq('status', 'paid')
    .gte(
      'created_at',
      new Date(Date.now() - 1000 * 60 * 60 * 24 * 30 * 6).toISOString(),
    );
  if (error) {
    logger.error('fetchMonthlyRevenue failed', error);
    return monthlyRevenueData;
  }
  const buckets = new Map<string, number>();
  (data ?? []).forEach((o: any) => {
    const d = new Date(o.paid_at ?? o.created_at);
    const key = d.toLocaleString('en-US', { month: 'short' });
    buckets.set(key, (buckets.get(key) ?? 0) + Number(o.amount ?? 0));
  });
  return Array.from(buckets.entries()).map(([month, totalRevenue]) => ({
    month,
    totalRevenue,
    masterLicenseShare: Math.round(totalRevenue * 0.7),
    resellerShare: Math.round(totalRevenue * 0.3),
  }));
};
